import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { FiEdit, FiTrash2, FiSearch, FiFileText, FiPlus, FiBookOpen } from 'react-icons/fi';
import { useAcademicYear } from '../contexts/AcademicYearContext';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';

const ProfesseursPage = () => {
  //const { user } = useAuth();
  const { selectedYear, loading: yearLoading } = useAcademicYear();
  const [professeurs, setProfesseurs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nom: '',
    prenom: '',
    datenaiss: '',
    sexe: '',
    telmob1: '',
    telmob2: '',
    email: '',
    adresse: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState([]);

  // Pour la gestion des qualifications
  const [qualifModalOpen, setQualifModalOpen] = useState(false);
  const [qualifProf, setQualifProf] = useState(null);
  const [qualifs, setQualifs] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [qualifForm, setQualifForm] = useState({ qualification_id: '', annee_obtention: '', etablissement: '' });
  const [qualifEditing, setQualifEditing] = useState(null);
  const [showQualifForm, setShowQualifForm] = useState(false);

  // Pour la gestion des classes et matières enseignées
  const [classesModalOpen, setClassesModalOpen] = useState(false);
  const [classesProf, setClassesProf] = useState(null);
  const [professeurClasses, setProfesseurClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  const fetchProfesseurs = async () => {
    const res = await api.get('/professeurs');
    setProfesseurs(res.data.data || []);
  };

  // Gestion des classes et matières enseignées
  const openClassesModal = async (prof) => {
    setClassesProf(prof);
    setClassesModalOpen(true);
    setProfesseurClasses([]);
    setLoadingClasses(true);

    // Attendre que l'année soit chargée si nécessaire
    if (yearLoading) {
      // L'année est en cours de chargement, attendre un peu
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Utiliser directement l'année sélectionnée dans l'entête
    if (selectedYear && prof) {
      try {
        const res = await api.get(`/professeurs/${prof.id}/classes-enseignées/${selectedYear.id}`);
        setProfesseurClasses(res.data.data || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des classes enseignées:', error);
        setProfesseurClasses([]);
      } finally {
        setLoadingClasses(false);
      }
    } else {
      setLoadingClasses(false);
    }
  };

  const closeClassesModal = () => {
    setClassesModalOpen(false);
    setClassesProf(null);
    setProfesseurClasses([]);
    setLoadingClasses(false);
  };

  useEffect(() => {
    fetchProfesseurs();
  }, []);

  useEffect(() => {
    setFiltered(
      professeurs.filter(p => {
        const nom = (p.nom || '').toLowerCase();
        const prenom = (p.prenom || '').toLowerCase();
        const term = searchTerm.toLowerCase();
        return nom.includes(term) || prenom.includes(term);
      })
    );
  }, [searchTerm, professeurs]);

  const handleEdit = (prof) => {
    setEditing(prof);
    setForm({
      nom: prof.nom || '',
      prenom: prof.prenom || '',
      datenaiss: prof.datenaiss || '',
      sexe: prof.sexe || '',
      telmob1: prof.telmob1 || '',
      telmob2: prof.telmob2 || '',
      email: prof.email || '',
      adresse: prof.adresse || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer ce professeur ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/professeurs/${id}`);
      fetchProfesseurs();
      Swal.fire('Supprimé !', 'Le professeur a été supprimé.', 'success');
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await api.put(`/professeurs/${editing.id}`, form);
    } else {
      await api.post('/professeurs', form);
    }
    setModalOpen(false);
    setEditing(null);
    setForm({
      nom: '', prenom: '', datenaiss: '', sexe: '', telmob1: '', telmob2: '', email: '', adresse: ''
    });
    fetchProfesseurs();
  };

  // Gestion qualifications
  const openQualifModal = async (prof) => {
    setQualifProf(prof);
    setQualifModalOpen(true);
    setQualifEditing(null);
    setShowQualifForm(false);
    setQualifForm({ qualification_id: '', annee_obtention: '', etablissement: '' });
    const [qRes, qualifRes, matRes, nivRes] = await Promise.all([
      api.get(`/prof-qualif/by-prof/${prof.id}`),
      api.get('/qualifications'),
      api.get('/matieres'),
      api.get('/niveau-qualif-prof'),
    ]);
    setQualifs(qRes.data.data || []);
    setQualifications(qualifRes.data.data || []);
    setMatieres(matRes.data.data || []);
    setNiveaux(nivRes.data.data || []);
  };
  const closeQualifModal = () => {
    setQualifModalOpen(false);
    setQualifProf(null);
    setQualifEditing(null);
    setShowQualifForm(false);
    setQualifForm({ qualification_id: '', annee_obtention: '', etablissement: '' });
    setQualifs([]);
    setQualifications([]);
    setMatieres([]);
    setNiveaux([]);
  };
  const handleQualifEdit = (qualif) => {
    setQualifEditing(qualif);
    setShowQualifForm(true);
    setQualifForm({
      qualification_id: qualif.qualification_id || '',
      annee_obtention: qualif.annee_obtention || '',
      etablissement: qualif.etablissement || ''
    });
  };
  const handleQualifDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette qualification ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/prof-qualif/${id}`);
      openQualifModal(qualifProf);
      Swal.fire('Supprimé !', 'La qualification a été supprimée.', 'success');
    }
  };
  const handleQualifModalSubmit = async (e) => {
    e.preventDefault();
    if (qualifEditing) {
      await api.put(`/prof-qualif/${qualifEditing.id}`, {
        ...qualifForm,
        professeur_id: qualifProf.id
      });
    } else {
      await api.post('/prof-qualif', {
        ...qualifForm,
        professeur_id: qualifProf.id
      });
    }
    openQualifModal(qualifProf);
    setQualifEditing(null);
    setShowQualifForm(false);
    setQualifForm({ qualification_id: '', annee_obtention: '', etablissement: '' });
  };

  return (
    <div>
      {/* Titre + bouton alignés */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold dark:text-gray-200">Gestion des professeurs</h1>
        <ConditionalButton
          entity="professeurs"
          permission="create"
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          onClick={() => { setEditing(null); setForm({ nom: '', prenom: '', datenaiss: '', sexe: '', telmob1: '', telmob2: '', email: '', adresse: '' }); setModalOpen(true); }}
        >
          Ajouter un professeur
        </ConditionalButton>
      </div>

      {/* Search Bar harmonisée */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un professeur..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded shadow">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
          <thead className="bg-gray-200 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Nom</th>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Prénom</th>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Date de naissance</th>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Sexe</th>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Tél. mobile 1</th>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Tél. mobile 2</th>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Email</th>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Adresse</th>
              <th className="px-4 py-2 text-center text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {filtered.map((prof) => (
              <tr key={prof.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{prof.nom}</td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{prof.prenom}</td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{prof.datenaiss}</td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{prof.sexe}</td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{prof.telmob1}</td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{prof.telmob2}</td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{prof.email}</td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{prof.adresse}</td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <ConditionalActions
                      entity="professeurs"
                      onEdit={() => handleEdit(prof)}
                      onDelete={() => handleDelete(prof.id)}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash2}
                      className="flex space-x-1"
                    />
                    <button
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded transition-colors"
                      onClick={() => openQualifModal(prof)}
                      title="Qualifications"
                    >
                      <FiFileText className="h-4 w-4" />
                    </button>
                    <button
                      className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 p-1 rounded transition-colors"
                      onClick={() => openClassesModal(prof)}
                      title="Classes et matières enseignées"
                    >
                      <FiBookOpen className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-4xl">
            <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">{editing ? 'Modifier' : 'Ajouter'} un professeur</h2>
            <form onSubmit={handleModalSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Nom</label>
                  <input type="text" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Prénom</label>
                  <input type="text" value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Date de naissance</label>
                  <input type="date" value={form.datenaiss} onChange={e => setForm({ ...form, datenaiss: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Sexe</label>
                  <select value={form.sexe} onChange={e => setForm({ ...form, sexe: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                    <option value="">-- Sélectionner --</option>
                    <option value="M">Masculin</option>
                    <option value="F">Féminin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Tél. mobile 1</label>
                  <input type="text" value={form.telmob1} onChange={e => setForm({ ...form, telmob1: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Tél. mobile 2</label>
                  <input type="text" value={form.telmob2} onChange={e => setForm({ ...form, telmob2: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Adresse</label>
                  <input type="text" value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600">Annuler</button>
                <ConditionalButton
                  entity="professeurs"
                  permission={editing ? "update" : "create"}
                  type="submit"
                  className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
                >
                  Enregistrer
                </ConditionalButton>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal Qualifications Professeur */}
      {qualifModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-4xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold dark:text-gray-200">Qualifications de {qualifProf?.nom} {qualifProf?.prenom}</h2>
              <div className="flex items-center gap-2">
                <button
                  className="px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center"
                  onClick={() => { setQualifEditing(null); setShowQualifForm(true); setQualifForm({ qualification_id: '', annee_obtention: '', etablissement: '' }); }}
                  title="Ajouter une qualification"
                >
                  <FiPlus />
                </button>
                <button className="px-2 py-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors" onClick={closeQualifModal}>Fermer</button>
              </div>
            </div>
            {/* Formulaire ajout/édition qualification */}
            {showQualifForm && (
              <form onSubmit={handleQualifModalSubmit} className="mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-gray-300">Qualification (Matière - Niveau)</label>
                    <select value={qualifForm.qualification_id} onChange={e => setQualifForm({ ...qualifForm, qualification_id: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required>
                      <option value="">-- Sélectionner --</option>
                      {qualifications.map(q => {
                        const mat = matieres.find(m => m.id === q.matiere_id);
                        const niv = niveaux.find(n => n.id === q.niveauqualif_id);
                        return (
                          <option key={q.id} value={q.id}>
                            {(mat ? mat.lib_matiere : 'Matière inconnue') + ' - ' + (niv ? niv.niveau : 'Niveau inconnu')}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-gray-300">Année d'obtention</label>
                    <input type="text" value={qualifForm.annee_obtention} onChange={e => setQualifForm({ ...qualifForm, annee_obtention: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" maxLength={4} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1 dark:text-gray-300">Établissement</label>
                    <input type="text" value={qualifForm.etablissement} onChange={e => setQualifForm({ ...qualifForm, etablissement: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button type="button" onClick={() => { setQualifEditing(null); setQualifForm({ qualification_id: '', annee_obtention: '', etablissement: '' }); }} className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600">Annuler</button>
                  <button type="submit" className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700">Enregistrer</button>
                </div>
              </form>
            )}
            <div className="flex justify-center mb-4">
              <div className="w-1/3 max-w-lg overflow-x-auto rounded shadow">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                <thead className="bg-gray-200 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Qualification</th>
                    <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Année</th>
                    <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Établissement</th>
                    <th className="px-4 py-2 text-right text-xs font-bold uppercase text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {qualifs.map((qualif) => {
                    const qualification = qualifications.find(q => q.id === qualif.qualification_id) || {};
                    const mat = matieres.find(m => m.id === qualification.matiere_id) || {};
                    const niv = niveaux.find(n => n.id === qualification.niveauqualif_id) || {};
                    return (
                      <tr key={qualif.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{mat.lib_matiere} - {niv.niveau}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{qualif.annee_obtention}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{qualif.etablissement}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 mr-3 transition-colors" onClick={() => handleQualifEdit(qualif)} title="Modifier">
                            <FiEdit />
                          </button>
                          <button 
                            onClick={() => handleQualifDelete(qualif.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors"
                            title="Supprimer"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Classes et Matières Enseignées */}
      {classesModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold flex items-center dark:text-gray-200">
                <FiBookOpen className="mr-2" />
                Classes et matières enseignées - {classesProf?.nom} {classesProf?.prenom}
              </h2>
              <button
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                onClick={closeClassesModal}
              >
                Fermer
              </button>
            </div>

            {/* Affichage de l'année en cours */}
            <div className="mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-center">
                  <FiBookOpen className="mr-2 text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                      Année scolaire sélectionnée: {yearLoading ? 'Chargement...' : (selectedYear?.annee_scolaire || 'Non définie')}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      {yearLoading ? 'Chargement de l\'année scolaire...' : 'Affichage des classes et matières pour l\'année sélectionnée dans l\'entête'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Affichage des résultats */}
            {loadingClasses ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Chargement des données...</p>
              </div>
            ) : selectedYear && (
              <div>
                {professeurClasses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FiBookOpen className="mx-auto h-12 w-12 mb-4 text-gray-300" />
                    <p className="text-lg">Aucune classe enseignée trouvée pour cette année scolaire</p>
                    <p className="text-sm mt-2">Le professeur n'a pas encore été assigné à des classes pour l'année sélectionnée.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-300 uppercase tracking-wider">
                            Classe
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-300 uppercase tracking-wider">
                            Matière
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-300 uppercase tracking-wider">
                            Cycle
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-300 uppercase tracking-wider">
                            Niveau
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-300 uppercase tracking-wider">
                            Enseignement
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-bold text-gray-900 dark:text-gray-300 uppercase tracking-wider">
                            Statut
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {professeurClasses.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                              {item.classe || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                              {item.matiere || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {item.cycle || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {item.niveau || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {item.enseignement || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                Actif
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {(!selectedYear && !yearLoading) && (
              <div className="text-center py-12 text-gray-500">
                <FiBookOpen className="mx-auto h-16 w-16 mb-4 text-gray-300" />
                <p className="text-lg">Aucune année scolaire sélectionnée</p>
                <p className="text-sm mt-2">Veuillez sélectionner une année scolaire dans l'en-tête de l'application.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfesseursPage;
